import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCompany } from "../../context/CompanyContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import "./GstTaxRegister.css";
import { TableRowsSkeleton } from "../Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // plugin already applied or fallback
}

const RAW_RECORDS = [
  {
    id: 1,
    baseDayRatio: 0.04,
    voucherNo: "1",
    type: "Sales",
    partyName: "XYZ Traders",
    gstin: "07AAACX1234B1Z5",
    taxableValue: 250000.0,
    cgst: 15000.0,
    cgstRate: 6,
    sgst: 15000.0,
    sgstRate: 6,
    igst: null,
    totalTax: 30000.0,
  },
  {
    id: 2,
    baseDayRatio: 0.15,
    voucherNo: "2",
    type: "Sales",
    partyName: "ABC Industries",
    gstin: "27AABCA5678C1Z3",
    taxableValue: 500000.0,
    cgst: 30000.0,
    cgstRate: 6,
    sgst: 30000.0,
    sgstRate: 6,
    igst: null,
    totalTax: 60000.0,
  },
  {
    id: 3,
    baseDayRatio: 0.30,
    voucherNo: "3",
    type: "Sales",
    partyName: "Star Suppliers",
    gstin: "33AACCS3456E1Z7",
    taxableValue: 175000.0,
    cgst: 10500.0,
    cgstRate: 6,
    sgst: 10500.0,
    sgstRate: 6,
    igst: null,
    totalTax: 21000.0,
  },
  {
    id: 4,
    baseDayRatio: 0.50,
    voucherNo: "4",
    type: "Sales",
    partyName: "National Traders",
    gstin: "19AACCN2345G1Z6",
    taxableValue: 800000.0,
    cgst: 48000.0,
    cgstRate: 6,
    sgst: 48000.0,
    sgstRate: 6,
    igst: null,
    totalTax: 96000.0,
  },
  {
    id: 5,
    baseDayRatio: 0.70,
    voucherNo: "5",
    type: "Sales",
    partyName: "Prime Enterprises",
    gstin: "27AACFP2222J1Z4",
    taxableValue: 650000.0,
    cgst: null,
    sgst: null,
    igst: 78000.0,
    igstRate: 12,
    totalTax: 78000.0,
  },
  {
    id: 6,
    baseDayRatio: 0.85,
    voucherNo: "6",
    type: "Sales",
    partyName: "Galaxy Retailers",
    gstin: "09AABCG4444L1Z1",
    taxableValue: 325000.0,
    cgst: 19500.0,
    cgstRate: 6,
    sgst: 19500.0,
    sgstRate: 6,
    igst: null,
    totalTax: 39000.0,
  },
];

const generateInitialRecords = () => {
  const now = new Date();
  const year = now.getFullYear();
  const monthNum = String(now.getMonth() + 1).padStart(2, "0");
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthName = monthNames[now.getMonth()];
  const currentDay = Math.max(now.getDate(), 1);

  return RAW_RECORDS.map((item) => {
    let dayNum = Math.round(item.baseDayRatio * currentDay);
    if (dayNum < 1) dayNum = 1;
    if (dayNum > currentDay) dayNum = currentDay;
    const dayStr = String(dayNum).padStart(2, "0");
    return {
      ...item,
      date: `${dayStr}-${monthName}-${year}`,
      rawDate: `${year}-${monthNum}-${dayStr}`,
    };
  });
};

const INITIAL_RECORDS = generateInitialRecords();

const formatCurrency = (val, showDashForNull = true) => {
  if (val === null || val === undefined || isNaN(val) || Number(val) === 0) {
    return "";
  }
  return Number(val).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = String(dateStr).split(/[-/]/);
  if (parts.length === 3 && parts[0].length === 4) {
    const [year, month, day] = parts;
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const mIdx = parseInt(month, 10) - 1;
    return `${String(day).padStart(2, "0")}-${monthNames[mIdx] || month}-${year}`;
  }
  return dateStr;
};

const formatCurrentDateTime = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
};

const getCurrentFYFirstDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const startYear = m >= 4 ? y : y - 1;
  return `${startYear}-04-01`;
};

const getCurrentMonthFirstDate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
};

const getCurrentDateString = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function GstTaxRegister() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const handleRowClick = (row) => {
    if (row.voucherId) {
      navigate(`/transactions/modify-voucher?voucherId=${row.voucherId}`);
    } else if ((row.type || "").toLowerCase() === "sales" && (row.invoiceNo || row.rawVoucherNo || row.id)) {
      navigate(`/modify-invoice?edit=${encodeURIComponent(row.invoiceNo || row.rawVoucherNo || row.id)}`);
    } else if (row.voucherNo || row.invoiceNo) {
      navigate(`/transactions/modify-voucher?voucherNo=${encodeURIComponent(row.voucherNo || row.invoiceNo)}`);
    }
  };

  const [liveRecords, setLiveRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({ totalSales: 0, totalPurchase: 0 });

  const [companyInfo, setCompanyInfo] = useState({
    name: selectedCompany?.Name || "Dazy Toure Travel's",
    address: selectedCompany?.Address || "Sahakar Marg",
    gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "08AGCPS3005M1ZB",
    stateCode: selectedCompany?.StateCode || "08",
  });

  const fetchGstRecords = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/reports/gst-register/${companyId}`);
      if (res.data?.success) {
        setLiveRecords(res.data.data || []);
        if (res.data.company) {
          setCompanyInfo((prev) => ({
            ...prev,
            name: res.data.company.name || selectedCompany?.Name || prev.name,
            address: res.data.company.address || selectedCompany?.Address || prev.address,
            gstin: res.data.company.gstin || selectedCompany?.GSTNO || prev.gstin,
            stateCode: res.data.company.stateCode || prev.stateCode,
          }));
        }
        if (res.data.summary) {
          setSummaryData({
            totalSales: res.data.summary.totalSales || 0,
            totalPurchase: res.data.summary.totalPurchase || 0,
          });
        }
      }
    } catch (err) {
      console.error("Error loading GST register data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [BASE_API_URL, companyId, selectedCompany]);

  useEffect(() => {
    fetchGstRecords();
  }, [fetchGstRecords]);

  const [currentDateTime, setCurrentDateTime] = useState(formatCurrentDateTime);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(formatCurrentDateTime());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const [fromDate, setFromDate] = useState(getCurrentMonthFirstDate);
  const [toDate, setToDate] = useState(getCurrentDateString);
  const [gstType, setGstType] = useState("All");
  const [partySearch, setPartySearch] = useState("");
  const [sortAsc, setSortAsc] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [activeFilters, setActiveFilters] = useState({
    fromDate: getCurrentMonthFirstDate(),
    toDate: getCurrentDateString(),
    gstType: "All",
    partySearch: "",
  });

  const [previewModal, setPreviewModal] = useState({
    open: false,
    format: null, // "excel" | "pdf"
  });

  const handleApplyFilter = (e) => {
    if (e) e.preventDefault();
    setActiveFilters({
      fromDate,
      toDate,
      gstType,
      partySearch: partySearch.trim(),
    });
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    const defaultFrom = getCurrentMonthFirstDate();
    const defaultTo = getCurrentDateString();
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setGstType("All");
    setPartySearch("");
    setActiveFilters({
      fromDate: defaultFrom,
      toDate: defaultTo,
      gstType: "All",
      partySearch: "",
    });
    setCurrentPage(1);
  };

  const baseRecords = liveRecords.length > 0 ? liveRecords : INITIAL_RECORDS;

  const filteredData = useMemo(() => {
    return baseRecords.filter((item) => {
      if (activeFilters.fromDate && item.rawDate && item.rawDate < activeFilters.fromDate) return false;
      if (activeFilters.toDate && item.rawDate && item.rawDate > activeFilters.toDate) return false;

      if (activeFilters.gstType !== "All") {
        if (activeFilters.gstType === "CGST" && (!item.cgst || item.cgst <= 0)) {
          return false;
        }
        if (activeFilters.gstType === "IGST" && (!item.igst || item.igst <= 0)) {
          return false;
        }
        if (activeFilters.gstType === "SGST" && (!item.sgst || item.sgst <= 0)) {
          return false;
        }
        if (activeFilters.gstType === "Sales" && (item.type || "").toLowerCase() !== "sales") {
          return false;
        }
        if (activeFilters.gstType === "Purchase" && (item.type || "").toLowerCase() !== "purchase") {
          return false;
        }
      }

      if (activeFilters.partySearch) {
        const query = activeFilters.partySearch.toLowerCase();
        const matchesParty = (item.partyName || "").toLowerCase().includes(query);
        const matchesGstin = (item.gstin || "").toLowerCase().includes(query);
        const matchesVoucher = (item.voucherNo || "").toLowerCase().includes(query);
        const matchesInvoice = (item.invoiceNo || "").toLowerCase().includes(query);
        if (!matchesParty && !matchesGstin && !matchesVoucher && !matchesInvoice) return false;
      }

      return true;
    }).sort((a, b) => {
      const cmp = (a.rawDate || "").localeCompare(b.rawDate || "");
      return sortAsc ? cmp : -cmp;
    });
  }, [baseRecords, activeFilters, sortAsc]);

  const totals = useMemo(() => {
    let taxable = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let totalTax = 0;
    let salesAmount = 0;
    let purchaseAmount = 0;

    filteredData.forEach((row) => {
      const val = Number(row.taxableValue) || 0;
      taxable += val;
      cgst += Number(row.cgst) || 0;
      sgst += Number(row.sgst) || 0;
      igst += Number(row.igst) || 0;
      totalTax += Number(row.totalTax) || 0;

      if (row.type === "Sales") {
        salesAmount += val;
      } else if (row.type === "Purchase") {
        purchaseAmount += val;
      }
    });

    return {
      taxable,
      totalSales: salesAmount,
      totalPurchase: purchaseAmount,
      outputTax: totalTax,
      inputTax: 0,
      netPayable: totalTax,
      tableTaxable: taxable,
      tableCGST: cgst,
      tableSGST: sgst,
      tableIGST: igst,
      tableTotalTax: totalTax,
    };
  }, [filteredData]);

  // Dynamic discovery of unique tax rate columns (e.g. CGST 4%, CGST 2.5%, SGST 4%, SGST 2.5%, IGST...)
  const dynamicTaxColumns = useMemo(() => {
    const cgstRateSet = new Set();
    const sgstRateSet = new Set();
    const igstRateSet = new Set();

    filteredData.forEach((row) => {
      if (row.cgst !== null && row.cgstRate !== null && row.cgstRate !== undefined && Number(row.cgstRate) > 0) {
        cgstRateSet.add(Number(row.cgstRate));
      }
      if (row.sgst !== null && row.sgstRate !== null && row.sgstRate !== undefined && Number(row.sgstRate) > 0) {
        sgstRateSet.add(Number(row.sgstRate));
      }
      if (row.igst !== null && row.igstRate !== null && row.igstRate !== undefined && Number(row.igstRate) > 0) {
        igstRateSet.add(Number(row.igstRate));
      }
    });

    // Fallback to baseRecords if filteredData is empty
    if (cgstRateSet.size === 0 && sgstRateSet.size === 0 && igstRateSet.size === 0) {
      baseRecords.forEach((row) => {
        if (row.cgst !== null && row.cgstRate && Number(row.cgstRate) > 0) {
          cgstRateSet.add(Number(row.cgstRate));
        }
        if (row.sgst !== null && row.sgstRate && Number(row.sgstRate) > 0) {
          sgstRateSet.add(Number(row.sgstRate));
        }
        if (row.igst !== null && row.igstRate && Number(row.igstRate) > 0) {
          igstRateSet.add(Number(row.igstRate));
        }
      });
    }

    // Default standard rates if no transactions exist
    if (cgstRateSet.size === 0 && sgstRateSet.size === 0) {
      cgstRateSet.add(4);
      cgstRateSet.add(2.5);
      sgstRateSet.add(4);
      sgstRateSet.add(2.5);
    }

    const sortedCgst = Array.from(cgstRateSet).sort((a, b) => b - a);
    const sortedSgst = Array.from(sgstRateSet).sort((a, b) => b - a);
    const sortedIgst = Array.from(igstRateSet).sort((a, b) => b - a);

    const cols = [];

    // CGST Columns
    sortedCgst.forEach((rate) => {
      cols.push({
        id: `cgst_${rate}`,
        type: "CGST",
        rate,
        label: `CGST ${rate}%`,
        labelPdf: `CGST ${rate}%`,
        labelExcel: `CGST ${rate}%`,
        labelUi: `CGST ${rate}%`,
        getValue: (r) => {
          if (r.cgst !== null && r.cgst !== undefined && Number(r.cgstRate) === rate) {
            return Number(r.cgst) || 0;
          }
          return null;
        },
      });
    });

    // SGST Columns
    sortedSgst.forEach((rate) => {
      cols.push({
        id: `sgst_${rate}`,
        type: "SGST",
        rate,
        label: `SGST ${rate}%`,
        labelPdf: `SGST ${rate}%`,
        labelExcel: `SGST ${rate}%`,
        labelUi: `SGST ${rate}%`,
        getValue: (r) => {
          if (r.sgst !== null && r.sgst !== undefined && Number(r.sgstRate) === rate) {
            return Number(r.sgst) || 0;
          }
          return null;
        },
      });
    });

    // IGST Columns (If rates exist, create for each; if none, provide clean default IGST column)
    if (sortedIgst.length > 0) {
      sortedIgst.forEach((rate) => {
        cols.push({
          id: `igst_${rate}`,
          type: "IGST",
          rate,
          label: `IGST ${rate}%`,
          labelPdf: `IGST ${rate}%`,
          labelExcel: `IGST ${rate}%`,
          labelUi: `IGST ${rate}%`,
          getValue: (r) => {
            if (r.igst !== null && r.igst !== undefined && Number(r.igstRate) === rate) {
              return Number(r.igst) || 0;
            }
            return null;
          },
        });
      });
    } else {
      cols.push({
        id: "igst_default",
        type: "IGST",
        rate: 0,
        label: "IGST",
        labelPdf: "IGST",
        labelExcel: "IGST",
        labelUi: "IGST",
        getValue: (r) => {
          if (r.igst !== null && r.igst !== undefined && Number(r.igst) > 0) {
            return Number(r.igst) || 0;
          }
          return null;
        },
      });
    }

    return cols;
  }, [filteredData, baseRecords]);

  // Dynamic column totals calculation
  const dynamicTaxTotals = useMemo(() => {
    const sums = {};
    dynamicTaxColumns.forEach((col) => {
      sums[col.id] = 0;
    });
    filteredData.forEach((row) => {
      dynamicTaxColumns.forEach((col) => {
        const val = col.getValue(row);
        if (val !== null) {
          sums[col.id] += val;
        }
      });
    });
    return sums;
  }, [filteredData, dynamicTaxColumns]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;

  const handleExportExcel = () => {
    const activeCompName = String(companyInfo.name || selectedCompany?.Name || "Dazy Toure Travel's").toUpperCase();
    const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
    const dateRangeTitle = `GST / TAX REGISTER (${formatDisplayDate(activeFilters.fromDate)} to ${formatDisplayDate(activeFilters.toDate)})`;
    const periodSubtitle = `Period: ${formatDisplayDate(activeFilters.fromDate)} to ${formatDisplayDate(activeFilters.toDate)}  |  GST Type: ${activeFilters.gstType}${activeFilters.partySearch ? `  |  Party Filter: ${activeFilters.partySearch}` : ""
      }  |  As on: ${currentDateTime}`;

    const headers = [
      "Date",
      "Invoice No.",
      "Type",
      "Party Name",
      "GSTIN",
      "Taxable",
      ...dynamicTaxColumns.map((col) => col.labelExcel),
      "Total Tax",
    ];

    const dataRows = filteredData.map((row) => [
      row.date,
      row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id,
      row.type || "Sales",
      row.partyName,
      row.gstin && row.gstin !== "-" ? row.gstin : "URP",
      formatCurrency(row.taxableValue, false),
      ...dynamicTaxColumns.map((col) => {
        const val = col.getValue(row);
        return val !== null ? formatCurrency(val, false) : "-";
      }),
      formatCurrency(row.totalTax, false),
    ]);

    const totalRow = [
      "Total",
      "",
      "",
      "",
      "",
      formatCurrency(totals.tableTaxable, false),
      ...dynamicTaxColumns.map((col) => formatCurrency(dynamicTaxTotals[col.id] || 0, false)),
      formatCurrency(totals.tableTotalTax, false),
    ];

    const wsData = [
      [activeCompName],
      [compDetails],
      [dateRangeTitle],
      [periodSubtitle],
      [],
      headers,
      ...dataRows,
      totalRow,
      [],
      ["etour - Design & Develop by Response Infoway"],
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const lastColIndex = headers.length - 1;
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastColIndex } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: lastColIndex } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: lastColIndex } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: lastColIndex } },
    ];

    const baseCols = [
      { wch: 13 }, // Date
      { wch: 13 }, // Invoice No
      { wch: 10 }, // Type
      { wch: 30 }, // Party Name
      { wch: 17 }, // GSTIN
      { wch: 16 }, // Taxable
    ];
    const taxColWidths = dynamicTaxColumns.map(() => ({ wch: 14 }));
    const totalTaxWidth = [{ wch: 16 }];
    ws["!cols"] = [...baseCols, ...taxColWidths, ...totalTaxWidth];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GST_Tax_Register");
    XLSX.writeFile(
      wb,
      `GST_Tax_Register_${formatDisplayDate(activeFilters.fromDate)}_to_${formatDisplayDate(activeFilters.toDate)}.xlsx`
    );
  };

  const generatePDFDoc = () => {
    const doc = new jsPDF("landscape", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const activeCompName = String(companyInfo.name || selectedCompany?.Name || "Dazy Toure Travel's").toUpperCase();
    const compAddress = companyInfo.address ? String(companyInfo.address) : "";
    const compGstin = companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "";
    const compState = companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "";
    const compLine2 = [compAddress, compGstin, compState].filter(Boolean).join("  |  ");

    const reportTitle = "GST / TAX REGISTER REPORT";

    // 1. Company Name Header (Centered, Bold, 15pt)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(0, 0, 0);
    doc.text(activeCompName, pageWidth / 2, 26, { align: "center" });

    // 2. Company Details (Address, GSTIN, State)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(40, 40, 40);
    doc.text(compLine2, pageWidth / 2, 38, { align: "center" });

    // 3. Report Title Underlined
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(0, 0, 0);
    doc.text(reportTitle, pageWidth / 2, 51, { align: "center" });
    const titleWidth = doc.getTextWidth(reportTitle);
    doc.setLineWidth(0.75);
    doc.setDrawColor(0, 0, 0);
    doc.line(pageWidth / 2 - titleWidth / 2, 53, pageWidth / 2 + titleWidth / 2, 53);

    // 4. Solid formal divider line above Meta bar
    doc.setLineWidth(1.5);
    doc.setDrawColor(0, 0, 0);
    doc.line(24, 61, pageWidth - 24, 61);

    // 5. Meta Information Bar (Left and Right aligned)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    const filterText = `Period: ${formatDisplayDate(activeFilters.fromDate)} to ${formatDisplayDate(activeFilters.toDate)}  |  GST Type: ${activeFilters.gstType}${activeFilters.partySearch ? `  |  Filter: "${activeFilters.partySearch}"` : ""
      }`;
    doc.text(filterText, 24, 72);

    const metaRight = `Date: ${currentDateTime}  |  Records: ${filteredData.length}`;
    doc.text(metaRight, pageWidth - 24, 72, { align: "right" });

    // 6. Solid formal divider line below Meta bar
    doc.setLineWidth(0.75);
    doc.setDrawColor(0, 0, 0);
    doc.line(24, 77, pageWidth - 24, 77);

    const tableColumns = [
      "Date",
      "Invoice No.",
      "Type",
      "Party Name",
      "GSTIN",
      "Taxable",
      ...dynamicTaxColumns.map((col) => col.labelPdf),
      "Total Tax",
    ];

    const tableRows = filteredData.map((row) => [
      row.date,
      row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id,
      row.type || "Sales",
      row.partyName,
      row.gstin && row.gstin !== "-" ? row.gstin : "URP",
      formatCurrency(row.taxableValue, false),
      ...dynamicTaxColumns.map((col) => {
        const val = col.getValue(row);
        return val !== null ? formatCurrency(val, false) : "-";
      }),
      formatCurrency(row.totalTax, false),
    ]);

    tableRows.push([
      "Total",
      "",
      "",
      "",
      "",
      formatCurrency(totals.tableTaxable, false),
      ...dynamicTaxColumns.map((col) => formatCurrency(dynamicTaxTotals[col.id] || 0, false)),
      formatCurrency(totals.tableTotalTax, false),
    ]);

    // Intelligent column width & font scaling based on count of dynamic tax columns
    const numTaxCols = dynamicTaxColumns.length;
    const totalCols = tableColumns.length;

    // Available table width = pageWidth - leftMargin(24) - rightMargin(24) = 793.89 pt
    const totalAvailableWidth = pageWidth - 48;

    let fontSize = 7.5;
    let headFontSize = 7.5;
    let padding = { top: 3.5, bottom: 3.5, left: 2.5, right: 2.5 };

    if (totalCols > 13) {
      fontSize = 6.2;
      headFontSize = 6.2;
      padding = { top: 2.5, bottom: 2.5, left: 1.5, right: 1.5 };
    } else if (totalCols > 11) {
      fontSize = 7.0;
      headFontSize = 7.0;
      padding = { top: 3, bottom: 3, left: 2, right: 2 };
    }

    const dateWidth = totalCols > 12 ? 50 : 56;
    const invWidth = totalCols > 12 ? 52 : 58;
    const typeWidth = totalCols > 12 ? 36 : 42;
    const partyNameWidth = totalCols > 12 ? 140 : 155;
    const gstinWidth = totalCols > 12 ? 78 : 86;
    const taxableWidth = totalCols > 12 ? 58 : 66;
    const totalTaxWidthPt = totalCols > 12 ? 58 : 66;

    const baseFixedTotal = dateWidth + invWidth + typeWidth + partyNameWidth + gstinWidth + taxableWidth + totalTaxWidthPt;
    const remainingForTax = Math.max(0, totalAvailableWidth - baseFixedTotal);
    const taxColWidth = numTaxCols > 0 ? Number((remainingForTax / numTaxCols).toFixed(2)) : 50;

    const colStyles = {
      0: { cellWidth: dateWidth, halign: "center" },
      1: { cellWidth: invWidth, halign: "center", fontStyle: "bold" },
      2: { cellWidth: typeWidth, halign: "center" },
      3: { cellWidth: partyNameWidth, halign: "left" },
      4: { cellWidth: gstinWidth, halign: "center", font: "helvetica", fontStyle: "bold", textColor: [0, 0, 0] },
      5: { cellWidth: taxableWidth, halign: "center" },
    };

    // Add dynamic tax columns to columnStyles centered
    dynamicTaxColumns.forEach((_, idx) => {
      colStyles[6 + idx] = { cellWidth: taxColWidth, halign: "center" };
    });

    // Total Tax column style
    colStyles[6 + numTaxCols] = { cellWidth: totalTaxWidthPt, halign: "center", fontStyle: "bold" };

    const tableOptions = {
      head: [tableColumns],
      body: tableRows,
      startY: 83,
      margin: { left: 24, right: 24, bottom: 30 },
      theme: "grid",
      styles: {
        fontSize,
        cellPadding: padding,
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        valign: "middle",
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: headFontSize,
        lineColor: [0, 0, 0],
        lineWidth: 0.75,
        valign: "middle",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: colStyles,
      didParseCell: (data) => {
        // Party name stays left-aligned, all other columns centered
        if (data.column.index === 3) {
          data.cell.styles.halign = "left";
        } else {
          data.cell.styles.halign = "center";
        }

        // Total row styling
        if (data.section === "body" && data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.textColor = [0, 0, 0];
          data.cell.styles.lineWidth = 0.75;
          data.cell.styles.halign = "center";
        }
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        doc.line(24, pageHeight - 20, pageWidth - 24, pageHeight - 20);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text("etour - Design & Develop by Response Infoway", 24, pageHeight - 10);
        doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 24, pageHeight - 10, { align: "right" });
      },
    };

    if (typeof doc.autoTable === "function") {
      doc.autoTable(tableOptions);
    } else if (typeof autoTable === "function") {
      autoTable(doc, tableOptions);
    } else if (autoTable && typeof autoTable.default === "function") {
      autoTable.default(doc, tableOptions);
    }

    return doc;
  };

  const handleExportPDF = () => {
    const doc = generatePDFDoc();
    doc.save(
      `GST_Tax_Register_${formatDisplayDate(activeFilters.fromDate)}_to_${formatDisplayDate(activeFilters.toDate)}.pdf`
    );
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="dashboard-wrapper gst_page_wrapper">
      {/* Top Header & Breadcrumb */}
      <div className="gst_breadcrumb_bar d-flex justify-content-between align-items-center">
        <div>
          <span>Reports &gt;</span> GST / Tax Register
        </div>
        <div className="gst_as_on_badge">
          <i className="fa-regular fa-calendar-days"></i>
          <span>As on: {currentDateTime}</span>
        </div>
      </div>

      {/* 1. Filter Bar */}
      <div className="gst_filter_bar">
        <form onSubmit={handleApplyFilter} className="d-flex align-items-center gap-2 flex-wrap w-100">
          <div className="d-flex align-items-center gap-1">
            <span className="filter-label">From:</span>
            <input
              type="date"
              className="filter-input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
              style={{ width: 125 }}
            />
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>–</span>
            <span className="filter-label">To:</span>
            <input
              type="date"
              className="filter-input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              required
              style={{ width: 125 }}
            />
          </div>

          <div className="d-flex align-items-center gap-1">
            <span className="filter-label">GST Type:</span>
            <select
              className="filter-select"
              value={gstType}
              onChange={(e) => setGstType(e.target.value)}
              style={{ width: 130 }}
            >
              <option value="All">All Invoices</option>
              <option value="CGST">CGST (Intra-State)</option>
              <option value="SGST">SGST (Intra-State)</option>
              <option value="IGST">IGST (Inter-State)</option>
            </select>
          </div>

          <div className="d-flex align-items-center gap-1 flex-grow-1" style={{ minWidth: 180 }}>
            <span className="filter-label">Party:</span>
            <div className="position-relative w-100">
              <input
                type="text"
                className="filter-input w-100"
                placeholder="Search party / GSTIN / Voucher..."
                value={partySearch}
                onChange={(e) => setPartySearch(e.target.value)}
                style={{ paddingRight: 20 }}
              />
              <i
                className="fa-solid fa-magnifying-glass position-absolute"
                style={{ right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 9, color: '#94a3b8' }}
              ></i>
            </div>
          </div>

          <div className="d-flex align-items-center gap-1 ms-auto">
            <button type="submit" className="filter-btn-apply">
              View
            </button>
            <button type="button" className="filter-btn-reset" onClick={handleResetFilter}>
              Reset
            </button>
            <button
              type="button"
              className="filter-btn-reset"
              onClick={fetchGstRecords}
              title="Refresh from database"
              style={{ padding: '0 8px' }}
            >
              <i className={`fa-solid fa-arrows-rotate ${isLoading ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </form>
      </div>

      {/* 2. KPI Summary Cards Row */}
      <div className="row g-1 gst_kpi_row">
        {/* Card 1: Taxable Value */}
        <div className="col-lg-3 col-md-6 col-12">
          <div className="gst_kpi_card">
            <div className="gst_kpi_icon_box icon_taxable">
              <i className="fa-regular fa-file-lines"></i>
            </div>
            <div className="gst_kpi_content">
              <span className="gst_kpi_label">Total Taxable Value</span>
              <div className="gst_kpi_value">₹ {formatCurrency(totals.taxable, false)}</div>
            </div>
          </div>
        </div>

        {/* Card 2: Total Sales */}
        <div className="col-lg-3 col-md-6 col-12">
          <div className="gst_kpi_card">
            <div className="gst_kpi_icon_box icon_output">
              <i className="fa-solid fa-arrow-up"></i>
            </div>
            <div className="gst_kpi_content">
              <span className="gst_kpi_label">Total Sales</span>
              <div className="gst_kpi_value">₹ {formatCurrency(totals.totalSales || totals.taxable, false)}</div>
            </div>
          </div>
        </div>

        {/* Card 3: Total Purchase */}
        <div className="col-lg-3 col-md-6 col-12">
          <div className="gst_kpi_card">
            <div className="gst_kpi_icon_box icon_input">
              <i className="fa-solid fa-arrow-down"></i>
            </div>
            <div className="gst_kpi_content">
              <span className="gst_kpi_label">Total Purchase</span>
              <div className="gst_kpi_value">₹ {formatCurrency(totals.totalPurchase || 0, false)}</div>
            </div>
          </div>
        </div>

        {/* Card 4: Total Tax */}
        <div className="col-lg-3 col-md-6 col-12">
          <div className="gst_kpi_card">
            <div className="gst_kpi_icon_box icon_payable">
              <i className="fa-solid fa-calculator"></i>
            </div>
            <div className="gst_kpi_content">
              <span className="gst_kpi_label">Total Tax</span>
              <div className="gst_kpi_value text_payable">
                ₹ {formatCurrency(totals.outputTax, false)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Table Card Section */}
      <div className="gst_table_card">
        <div className="gst_table_header justify-content-end">
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <button
              type="button"
              className="gst_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export Excel (.xlsx)"
            >
              <i className="fa-regular fa-file-excel"></i>
              <span>Export Excel</span>
            </button>

            <button
              type="button"
              className="gst_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export PDF"
            >
              <i className="fa-regular fa-file-pdf"></i>
              <span>Export PDF</span>
            </button>

            <button
              type="button"
              className="gst_export_btn btn_print"
              onClick={handlePrint}
              title="Print Register"
            >
              <i className="fa-solid fa-print"></i>
              <span>Print</span>
            </button>
          </div>
        </div>

        {/* Formal Printable Header (Visible during Print) */}
        <div className="gst_print_header d-none d-print-block text-center mb-3">
          <h2 className="mb-0 fw-bold text-uppercase" style={{ fontSize: "18px", color: "#000000", letterSpacing: "0.5px" }}>
            {companyInfo.name || selectedCompany?.Name || "DAZY TOURE TRAVEL'S"}
          </h2>
          <div style={{ fontSize: "11px", color: "#333333", marginTop: "2px" }}>
            {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
          </div>
          <div className="fw-bold mt-2" style={{ fontSize: "13px", color: "#000000", textDecoration: "underline" }}>
            GST / TAX REGISTER
          </div>
          <div style={{ fontSize: "10.5px", color: "#555555" }}>
            Period: {formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)}  |  As on: {currentDateTime}
          </div>
          <hr style={{ borderColor: "#000000", margin: "8px 0" }} />
        </div>

        {/* Table Container */}
        <div className="table-responsive">
          <table className="gst_custom_table">
            <thead>
              <tr>
                <th
                  className="col_date cursor-pointer user-select-none"
                  onClick={() => setSortAsc(!sortAsc)}
                  title="Sort by Date"
                >
                  <div className="d-flex align-items-center gap-1">
                    <span>Date</span>
                    <span className="fw-bold">{sortAsc ? "▲" : "▼"}</span>
                  </div>
                </th>
                <th className="col_voucher">Invoice No.</th>
                <th className="col_type">Type</th>
                <th className="col_party">Party Name</th>
                <th className="col_gstin">GSTIN</th>
                <th className="col_num text-center">Taxable Value (₹)</th>
                <th className="col_num text-end">CGST (₹)</th>
                <th className="col_num text-end">SGST (₹)</th>
                <th className="col_num text-end">IGST (₹)</th>
                <th className="col_num text-end">Total Tax (₹)</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableRowsSkeleton columns={10} rows={5} />
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="col_date">{row.date}</td>
                    <td className="col_voucher fw-semibold text-dark">
                      <div>{row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id}</div>
                    </td>
                    <td className="col_type">
                      <span
                        className="badge"
                        style={{
                          backgroundColor: (row.type || "").toLowerCase() === "purchase" ? "#2563eb" : "#16a34a",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "3px 8px",
                          fontSize: "11px",
                          fontWeight: 600,
                          boxShadow: "none",
                          display: "inline-block",
                        }}
                      >
                        {row.type || "Sales"}
                      </span>
                    </td>
                    <td className="col_party fw-medium text-dark" title={row.partyName}>{row.partyName}</td>
                    <td className="col_gstin text-secondary">
                      {row.gstin && row.gstin !== "-" ? (
                        <span className="fw-semibold text-dark">{row.gstin}</span>
                      ) : (
                        <span className="badge bg-light text-muted border" style={{ fontSize: "10px", fontWeight: 500 }}>
                          URP (Unregistered)
                        </span>
                      )}
                    </td>
                    <td className="col_num text-center fw-semibold text-dark">{Number(row.taxableValue) > 0 ? formatCurrency(row.taxableValue) : ""}</td>
                    <td className="col_num text-end text-dark">
                      {row.cgst !== null && row.cgst !== undefined && Number(row.cgst) > 0 ? (
                        <div>
                          <span>{formatCurrency(row.cgst)}</span>
                          <span className="ms-1" style={{ fontSize: "10.5px", color: "#64748b", fontWeight: 600 }}>
                            ({row.cgstRate || (row.taxRate / 2)}%)
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="col_num text-end text-dark">
                      {row.sgst !== null && row.sgst !== undefined && Number(row.sgst) > 0 ? (
                        <div>
                          <span>{formatCurrency(row.sgst)}</span>
                          <span className="ms-1" style={{ fontSize: "10.5px", color: "#64748b", fontWeight: 600 }}>
                            ({row.sgstRate || (row.taxRate / 2)}%)
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="col_num text-end text-dark">
                      {row.igst !== null && row.igst !== undefined && Number(row.igst) > 0 ? (
                        <div>
                          <span>{formatCurrency(row.igst)}</span>
                          <span className="ms-1" style={{ fontSize: "10.5px", color: "#64748b", fontWeight: 600 }}>
                            ({row.igstRate || row.taxRate}%)
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </td>
                    <td className="col_num text-end fw-bold text-dark">{Number(row.totalTax) > 0 ? formatCurrency(row.totalTax) : ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-muted">
                    <i className="fa-regular fa-folder-open fa-2x mb-2 text-secondary"></i>
                    <div>No matching GST records found for the selected period.</div>
                  </td>
                </tr>
              )}
            </tbody>
            {filteredData.length > 0 && (
              <tfoot>
                <tr className="gst_table_total_row">
                  <td colSpan={5} className="fw-bold text-dark">
                    Total
                  </td>
                  <td className="col_num text-end fw-bold text-dark">{formatCurrency(totals.tableTaxable, false)}</td>
                  <td className="col_num text-end fw-bold text-dark">{formatCurrency(totals.tableCGST, false)}</td>
                  <td className="col_num text-end fw-bold text-dark">{formatCurrency(totals.tableSGST, false)}</td>
                  <td className="col_num text-end fw-bold text-dark">{formatCurrency(totals.tableIGST, false)}</td>
                  <td className="col_num text-end fw-bold text-dark">{formatCurrency(totals.tableTotalTax, false)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Bar */}
        <div className="gst_table_footer">
          <div style={{ color: "#64748b" }}>
            Showing {filteredData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to{" "}
            {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} records
          </div>
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="gst_page_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              &lt;
            </button>
            <span className="gst_page_active_num">{currentPage}</span>
            <button
              type="button"
              className="gst_page_btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              &gt;
            </button>
            <div className="d-flex align-items-center gap-1 ms-1" style={{ color: "#64748b" }}>
              <span>Rows:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="filter-select py-0 px-1"
                style={{ height: 22, fontSize: 10.5 }}
              >
                <option value={14}>14</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal (Matching Dashboard Modal) */}
      {previewModal.open && (
        <div
          className="gst_preview_modal_overlay"
          onClick={() => setPreviewModal({ open: false, format: null })}
        >
          <div
            className="gst_preview_modal_card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="gst_preview_modal_header">
              <div className="d-flex align-items-center gap-2">
                <i
                  className={
                    previewModal.format === "excel"
                      ? "fa-regular fa-file-excel text-success fs-5"
                      : "fa-regular fa-file-pdf text-danger fs-5"
                  }
                ></i>
                <h3 className="m-0 text-white" style={{ fontSize: "16px", fontWeight: "700" }}>
                  {previewModal.format === "excel"
                    ? `Excel Sheet Preview — GST / Tax Register (${formatDisplayDate(activeFilters.fromDate)} to ${formatDisplayDate(activeFilters.toDate)})`
                    : `PDF Document Preview — GST / Tax Register (${formatDisplayDate(activeFilters.fromDate)} to ${formatDisplayDate(activeFilters.toDate)})`}
                </h3>
              </div>
              <button
                type="button"
                className="gst_preview_close_btn"
                onClick={() => setPreviewModal({ open: false, format: null })}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal Sub-Toolbar */}
            <div className="gst_preview_modal_toolbar">
              <div className="gst_preview_meta_badge">
                <span>Period: {formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)}</span>
                <span>•</span>
                <span>GST Type: {activeFilters.gstType}</span>
                <span>•</span>
                <span>As on: {currentDateTime}</span>
                <span>•</span>
                <span>{filteredData.length} records</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                {previewModal.format === "pdf" && (
                  <button
                    type="button"
                    className="gst_modal_action_btn btn_modal_print"
                    onClick={handlePrint}
                    title="Print PDF Document"
                  >
                    <i className="fa-solid fa-print me-1"></i> Print
                  </button>
                )}
                {previewModal.format === "excel" ? (
                  <button
                    type="button"
                    className="gst_modal_action_btn btn_modal_download_excel"
                    onClick={handleExportExcel}
                  >
                    <i className="fa-regular fa-file-excel me-1"></i> Download Excel (.xlsx)
                  </button>
                ) : (
                  <button
                    type="button"
                    className="gst_modal_action_btn btn_modal_download_pdf"
                    onClick={handleExportPDF}
                  >
                    <i className="fa-regular fa-file-pdf me-1"></i> Download PDF
                  </button>
                )}
              </div>
            </div>

            {/* Modal Body Canvas */}
            <div className="gst_preview_modal_body">
              {previewModal.format === "excel" ? (
                /* EXCEL SPREADSHEET PREVIEW (View & Only Download, Colorful) */
                <div className="excel_preview_container">
                  <div className="excel_tab_bar">
                    <span className="excel_tab_active">
                      <i className="fa-solid fa-table me-1 text-success"></i> GST_Tax_Register
                    </span>
                  </div>
                  <div className="excel_sheet_grid">
                    <table className="excel_table">
                      <thead>
                        <tr className="excel_col_header_row">
                          <th className="excel_row_num">#</th>
                          <th>A</th>
                          <th>B</th>
                          <th>C</th>
                          <th>D</th>
                          <th>E</th>
                          <th>F</th>
                          <th>G</th>
                          <th>H</th>
                          <th>I</th>
                          <th>J</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Row 1: Company Name */}
                        <tr className="excel_title_row">
                          <td className="excel_row_num">1</td>
                          <td colSpan={10} className="excel_title_cell" style={{ fontWeight: 800, fontSize: "14px" }}>
                            {String(companyInfo.name || selectedCompany?.Name || "DAZY TOURE TRAVEL'S").toUpperCase()}
                          </td>
                        </tr>
                        {/* Row 2: Subtitle */}
                        <tr className="excel_subtitle_row">
                          <td className="excel_row_num">2</td>
                          <td colSpan={10} className="excel_subtitle_cell" style={{ fontSize: "10.5px" }}>
                            {[companyInfo.address, companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "", companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : ""].filter(Boolean).join("   |   ")}
                          </td>
                        </tr>
                        {/* Row 3: Merged Title */}
                        <tr className="excel_title_row">
                          <td className="excel_row_num">3</td>
                          <td colSpan={10} className="excel_title_cell">
                            GST / Tax Register ({formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)})
                          </td>
                        </tr>
                        {/* Row 4: Subtitle */}
                        <tr className="excel_subtitle_row">
                          <td className="excel_row_num">4</td>
                          <td colSpan={10} className="excel_subtitle_cell">
                            Period: {formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)} | GST Type: {activeFilters.gstType} | Records: {filteredData.length} | As on: {currentDateTime}
                          </td>
                        </tr>
                        {/* Row 5: Blank */}
                        <tr>
                          <td className="excel_row_num">5</td>
                          <td colSpan={10} className="excel_blank_cell">&nbsp;</td>
                        </tr>
                        {/* Row 6: Column Headers */}
                        <tr className="excel_headers_row">
                          <td className="excel_row_num">6</td>
                          <td>Date</td>
                          <td>Invoice No.</td>
                          <td>Type</td>
                          <td>Party Name</td>
                          <td>GSTIN</td>
                          <td className="text-end">Taxable Value (₹)</td>
                          <td className="text-end">CGST (₹)</td>
                          <td className="text-end">SGST (₹)</td>
                          <td className="text-end">IGST (₹)</td>
                          <td className="text-end">Total Tax (₹)</td>
                        </tr>
                        {/* Rows 7+: Data Rows */}
                        {filteredData.map((row, idx) => (
                          <tr key={row.id} className={idx % 2 === 1 ? "excel_row_alt" : ""}>
                            <td className="excel_row_num">{7 + idx}</td>
                            <td>{row.date}</td>
                            <td className="fw-semibold text-dark">{row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id}</td>
                            <td>
                              <span className={`badge ${row.type === 'Sales' ? 'bg-success' : 'bg-primary'}`} style={{ fontSize: '10px' }}>
                                {row.type}
                              </span>
                            </td>
                            <td className="fw-medium">{row.partyName}</td>
                            <td className="font-monospace">
                              {row.gstin && row.gstin !== "-" ? (
                                row.gstin
                              ) : (
                                <span className="badge bg-light text-muted border" style={{ fontSize: "9.5px" }}>
                                  URP
                                </span>
                              )}
                            </td>
                            <td className="text-end fw-semibold">{formatCurrency(row.taxableValue, false)}</td>
                            <td className="text-end">
                              {row.cgst !== null ? (
                                <span>
                                  {formatCurrency(row.cgst, false)}{" "}
                                  <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                                    ({row.cgstRate || (row.taxRate / 2)}%)
                                  </span>
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-end">
                              {row.sgst !== null ? (
                                <span>
                                  {formatCurrency(row.sgst, false)}{" "}
                                  <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                                    ({row.sgstRate || (row.taxRate / 2)}%)
                                  </span>
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-end">
                              {row.igst !== null ? (
                                <span>
                                  {formatCurrency(row.igst, false)}{" "}
                                  <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                                    ({row.igstRate || row.taxRate}%)
                                  </span>
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="text-end fw-bold" style={{ color: "#064e48" }}>{formatCurrency(row.totalTax, false)}</td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="excel_total_row">
                          <td className="excel_row_num">{5 + filteredData.length}</td>
                          <td colSpan={5} className="fw-bold">Total</td>
                          <td className="text-end fw-bold">{formatCurrency(totals.tableTaxable, false)}</td>
                          <td className="text-end fw-bold">{formatCurrency(totals.tableCGST, false)}</td>
                          <td className="text-end fw-bold">{formatCurrency(totals.tableSGST, false)}</td>
                          <td className="text-end fw-bold">{formatCurrency(totals.tableIGST, false)}</td>
                          <td className="text-end fw-bold">{formatCurrency(totals.tableTotalTax, false)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* PDF DOCUMENT PREVIEW (Formal Black & White Professional Layout) */
                <div className="pdf_preview_container" style={{ background: "#ffffff", padding: "20px", border: "1px solid #cbd5e1" }}>
                  {/* Formal Header */}
                  <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "12px", marginBottom: "14px" }}>
                    <h3 style={{ margin: "0 0 4px 0", fontSize: "18px", fontWeight: "bold", textTransform: "uppercase", color: "#000000", letterSpacing: "0.5px" }}>
                      {companyInfo?.name || "DAZY TOURE TRAVEL'S"}
                    </h3>
                    <div style={{ fontSize: "11px", color: "#333333", lineHeight: 1.4 }}>
                      {companyInfo?.address && `${companyInfo.address} | `}
                      <strong>GSTIN:</strong> {companyInfo?.gstin || "08AGCPS3005M1ZB"}
                      {companyInfo?.stateName && ` | State: ${companyInfo.stateName} (${companyInfo?.stateCode || "08"})`}
                    </div>
                    <div style={{ marginTop: "8px", fontSize: "13px", fontWeight: "bold", textDecoration: "underline", color: "#000000" }}>
                      GST / TAX REGISTER REPORT
                    </div>
                  </div>

                  {/* Meta Bar */}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "10px", color: "#000000", borderBottom: "1px solid #000000", paddingBottom: "6px" }}>
                    <div>
                      <strong>Period:</strong> {formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)} &nbsp;|&nbsp; <strong>GST Type:</strong> {activeFilters.gstType}
                      {activeFilters.partySearch ? ` | Filter: "${activeFilters.partySearch}"` : ""}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <strong>Date:</strong> {currentDateTime} &nbsp;|&nbsp; <strong>Records:</strong> {filteredData.length}
                    </div>
                  </div>

                  {/* Formal Black & White Table */}
                  <div className="table-responsive">
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", color: "#000000", border: "1px solid #000000" }}>
                      <thead>
                        <tr style={{ background: "#f1f5f9", borderBottom: "1.5px solid #000000" }}>
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "center", width: "8%" }}>Date</th>
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "center", width: "8%" }}>Invoice No.</th>
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "center", width: "6%" }}>Type</th>
                          <th style={{ border: "1px solid #000000", padding: "5px 6px", textAlign: "left", width: "22%" }}>Party Name</th>
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "center", width: "12%" }}>GSTIN</th>
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "right", width: "10%" }}>Taxable (₹)</th>
                          {dynamicTaxColumns.map((col) => (
                            <th key={col.id} style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "right" }}>
                              {col.labelUi}
                            </th>
                          ))}
                          <th style={{ border: "1px solid #000000", padding: "5px 4px", textAlign: "right", width: "10%" }}>Total Tax (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((row, idx) => (
                          <tr key={row.id} style={{ borderBottom: "1px solid #cccccc", background: idx % 2 === 1 ? "#fafafa" : "#ffffff" }}>
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "center" }}>{row.date}</td>
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "center", fontWeight: "bold" }}>
                              {row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id}
                            </td>
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "center" }}>
                              {row.type}
                            </td>
                            <td style={{ border: "1px solid #cccccc", padding: "4px 6px" }}>{row.partyName}</td>
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "center", fontFamily: "monospace", fontSize: "9px" }}>
                              {row.gstin && row.gstin !== "-" ? row.gstin : "URP"}
                            </td>
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "right" }}>{formatCurrency(row.taxableValue, false)}</td>
                            {dynamicTaxColumns.map((col) => {
                              const val = col.getValue(row);
                              return (
                                <td key={col.id} style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "right" }}>
                                  {val !== null ? formatCurrency(val, false) : "-"}
                                </td>
                              );
                            })}
                            <td style={{ border: "1px solid #cccccc", padding: "4px", textAlign: "right", fontWeight: "bold" }}>{formatCurrency(row.totalTax, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "#f1f5f9", fontWeight: "bold", borderTop: "1.5px solid #000000" }}>
                          <td colSpan={5} style={{ border: "1px solid #000000", padding: "6px" }}>Total</td>
                          <td style={{ border: "1px solid #000000", padding: "6px", textAlign: "right" }}>{formatCurrency(totals.tableTaxable, false)}</td>
                          {dynamicTaxColumns.map((col) => (
                            <td key={col.id} style={{ border: "1px solid #000000", padding: "6px", textAlign: "right" }}>
                              {formatCurrency(dynamicTaxTotals[col.id] || 0, false)}
                            </td>
                          ))}
                          <td style={{ border: "1px solid #000000", padding: "6px", textAlign: "right" }}>{formatCurrency(totals.tableTotalTax, false)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Formal PDF Footer */}
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px", fontSize: "9px", color: "#555555", borderTop: "1px solid #cccccc", paddingTop: "6px" }}>
                    <span>eTour Business Management System — Confidential GST Tax Register Report</span>
                    <span>Page 1 of 1</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="gst_print_area d-none">
        {/* Company Header */}
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "10px", marginBottom: "12px" }}>
          <h2 style={{ margin: "0 0 4px 0", fontSize: "20px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo?.name || "DAZY TOURE TRAVEL'S"}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo?.address && `${companyInfo.address} | `}
            <strong>GSTIN:</strong> {companyInfo?.gstin || "08AGCPS3005M1ZB"}
            {companyInfo?.stateName && ` | State: ${companyInfo.stateName} (${companyInfo?.stateCode || "08"})`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "14px", fontWeight: "bold", textDecoration: "underline", color: "#000000" }}>
            GST / TAX REGISTER REPORT
          </div>
        </div>

        {/* Print Filter Meta */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Period:</strong> {formatDisplayDate(activeFilters.fromDate)} to {formatDisplayDate(activeFilters.toDate)} | <strong>GST Type:</strong> {activeFilters.gstType}
            {activeFilters.partySearch ? ` | Filter: "${activeFilters.partySearch}"` : ""}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {currentDateTime} | <strong>Records:</strong> {filteredData.length}
          </div>
        </div>

        {/* Full Data Table with dynamic percentage columns */}
        <table className="gst_print_table">
          <thead>
            <tr>
              <th style={{ width: "8%" }} className="text-center">Date</th>
              <th style={{ width: "9%" }} className="text-center">Invoice No.</th>
              <th style={{ width: "6%" }} className="text-center">Type</th>
              <th style={{ width: "22%" }}>Party Name</th>
              <th style={{ width: "12%" }} className="text-center">GSTIN</th>
              <th style={{ width: "10%" }} className="text-end">Taxable (₹)</th>
              {dynamicTaxColumns.map((col) => (
                <th key={col.id} className="text-end">
                  {col.labelUi}
                </th>
              ))}
              <th style={{ width: "10%" }} className="text-end">Total Tax (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((row, idx) => (
              <tr key={row.id} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td className="text-center">{row.date}</td>
                <td className="text-center fw-bold text-dark">
                  {row.displayInvoiceNo || row.invoiceNoNumeric || row.invoiceNo || row.rawVoucherNo || row.id}
                </td>
                <td className="text-center">
                  {row.type}
                </td>
                <td className="fw-medium text-dark">{row.partyName}</td>
                <td className="text-center font-monospace">
                  {row.gstin && row.gstin !== "-" ? (
                    row.gstin
                  ) : (
                    <span className="print_urp_badge">URP</span>
                  )}
                </td>
                <td className="text-end fw-semibold">{formatCurrency(row.taxableValue, false)}</td>
                {dynamicTaxColumns.map((col) => {
                  const val = col.getValue(row);
                  return (
                    <td key={col.id} className="text-end">
                      {val !== null ? formatCurrency(val, false) : "-"}
                    </td>
                  );
                })}
                <td className="text-end fw-bold print_total_tax_cell">{formatCurrency(row.totalTax, false)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td colSpan={5} className="fw-bold">Total</td>
              <td className="text-end fw-bold">{formatCurrency(totals.tableTaxable, false)}</td>
              {dynamicTaxColumns.map((col) => (
                <td key={col.id} className="text-end fw-bold">
                  {formatCurrency(dynamicTaxTotals[col.id] || 0, false)}
                </td>
              ))}
              <td className="text-end fw-bold">{formatCurrency(totals.tableTotalTax, false)}</td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="gst_print_footer">
          <span>eTour Business Management System — Confidential GST Statement</span>
          <span>Printed on: {currentDateTime}</span>
        </div>
      </div>
    </div>
  );
}
